import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Globe } from 'lucide-react';
import { PageSEO } from '@/components/SEO';
import axios from 'axios';

const DEFAULT_PROSTEKTER = [
  { name: 'Flytte Alt', url: 'https://flyttealt.no', domain: 'flyttealt.no', category: 'Flyttetjenester', description: 'Profesjonell flyttehjelp og flyttevask i hele Norge. Komplett nettside med bestillingsskjema og tjenesteoversikt.', services: ['Nettside', 'SEO'], image: null },
  { name: 'Flott Rent AS', url: 'https://flottrent.no', domain: 'flottrent.no', category: 'Rengjøring', description: 'Profesjonell rengjøringstjeneste med flyttevask, visningsvask og kontorvask. Nettside med fornøydgaranti og online bestilling.', services: ['Nettside', 'SEO'], image: null },
  { name: 'Oscars Pizzeria', url: 'https://oscarspizzeria.no', domain: 'oscarspizzeria.no', category: 'Restaurant', description: 'Pizzeria i Holter med online bestilling og levering. Nettside med meny, åpningstider og bestillingssystem.', services: ['Nettside'], image: null },
  { name: 'Herre Fast Food', url: 'https://herrefastfood.no', domain: 'herrefastfood.no', category: 'Restaurant', description: 'Fast food restaurant i Herre med pizza, burger og salater. Komplett nettside med online bestilling.', services: ['Nettside'], image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&q=80' },
  { name: 'Smakglød Torp', url: 'https://smakglød.no', domain: 'smakglød.no', category: 'Restaurant', description: 'Restaurant med smash burgere, kebab, pizza og grill. Online meny og bestillingssystem integrert.', services: ['Nettside'], image: null },
];

// Bruk REACT_APP_API_URL hvis satt (f.eks. for å synkronisere med admin på annet domene), ellers current origin
const API = typeof window !== 'undefined'
  ? `${process.env.REACT_APP_API_URL || window.location.origin}/api`
  : '/api';

function ProsjektBilde({ prosjekt }) {
  const [imageError, setImageError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const imageUrl = prosjekt.image;
  const logoUrl = prosjekt.domain ? `https://logo.clearbit.com/${prosjekt.domain}` : null;

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={prosjekt.name}
        className="w-full h-64 md:h-80 object-cover transition-transform duration-500 group-hover:scale-105"
        onError={() => setImageError(true)}
      />
    );
  }
  if (logoUrl && !logoError) {
    return (
      <img
        src={logoUrl}
        alt={`${prosjekt.name} logo`}
        className="w-full h-64 md:h-80 object-contain bg-muted p-8 transition-transform duration-500 group-hover:scale-105"
        onError={() => setLogoError(true)}
      />
    );
  }
  return (
    <div className="w-full h-64 md:h-80 bg-muted flex items-center justify-center">
      <span className="font-syne font-bold text-6xl text-muted-foreground/50">
        {prosjekt.name.charAt(0)}
      </span>
    </div>
  );
}

export default function Prosjekter() {
  const [prosjekter, setProsjekter] = useState(DEFAULT_PROSTEKTER);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cache-busting og no-cache for å unngå utdatert data fra CDN/nettleser
    axios.get(`${API}/projects`, {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    }).then((res) => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setProsjekter(res.data);
      }
    }).catch(() => { }).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageSEO
        title="Prosjekter – Se våre leveranser for bedrifter i Norge"
        path="/prosjekter"
        description="Portfolio: Profesjonelle nettsider og SEO-prosjekter vi har levert for bedrifter over hele Norge. Se våre suksesshistorier her."
      />
      {/* Hero Section */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="prosjekter-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Våre prosjekter
            </p>
            <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
              Se hva vi har skapt for våre kunder
            </h1>
            <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
              Her er et utvalg av nettsider vi har utviklet. Hver nettside er skreddersydd for å møte kundens behov og mål.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 md:py-32" data-testid="prosjekter-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {prosjekter.map((prosjekt, index) => (
              <a
                key={prosjekt.id ?? index}
                href={prosjekt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                data-testid={`prosjekt-${index}`}
              >
                <div className="relative overflow-hidden mb-6 rounded-2xl shadow-lg ring-1 ring-primary/5">
                  <ProsjektBilde prosjekt={prosjekt} />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors duration-500 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-accent text-accent-foreground px-6 py-3 flex items-center gap-2 font-syne font-bold text-sm uppercase rounded-lg">
                      Besøk nettside
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-accent text-accent-foreground px-4 py-2 font-manrope text-xs font-medium uppercase tracking-wide rounded-md">
                      {prosjekt.category}
                    </span>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-syne font-bold text-2xl text-primary group-hover:text-accent transition-colors duration-300 mb-2">
                      {prosjekt.name}
                    </h3>
                    <p className="font-manrope text-sm text-muted-foreground leading-relaxed mb-4">
                      {prosjekt.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(prosjekt.services || []).map((service, idx) => (
                        <span
                          key={idx}
                          className="bg-muted px-3 py-1 font-manrope text-xs text-muted-foreground rounded-full"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Globe className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors duration-300 flex-shrink-0 mt-1" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary text-primary-foreground" data-testid="prosjekter-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">5+</p>
              <p className="font-manrope text-sm text-primary-foreground/70">Nettsider levert</p>
            </div>
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">100%</p>
              <p className="font-manrope text-sm text-primary-foreground/70">Fornøyde kunder</p>
            </div>
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">3+</p>
              <p className="font-manrope text-sm text-primary-foreground/70">Bransjer</p>
            </div>
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">24/7</p>
              <p className="font-manrope text-sm text-primary-foreground/70">Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-muted" data-testid="prosjekter-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Vil du ha en nettside som dette?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt med oss for en uforpliktende samtale om hvordan vi kan hjelpe din bedrift å vokse digitalt.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="prosjekter-cta-button"
          >
            Start ditt prosjekt
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
